// Shared company-form constants — used by CompanyModal (create + edit) and the
// Accounts page (status/industry filters). Extracted from Accounts.jsx so the
// create and edit company modals share one source of truth (issue #177).

export const INDUSTRIES = [
    // Technology & Software
    'Computer Software', 'Information Technology & Services', 'Internet', 'Computer Hardware',
    'Semiconductors', 'Telecommunications', 'Computer Networking', 'Cybersecurity',
    'Artificial Intelligence', 'Cloud Computing', 'SaaS',
    // Financial
    'Financial Services', 'Banking', 'Insurance', 'Investment Management', 'Venture Capital & Private Equity',
    'Accounting', 'Capital Markets',
    // Healthcare & Life Sciences
    'Healthcare', 'Hospital & Health Care', 'Medical Devices', 'Pharmaceuticals', 'Biotechnology',
    'Health, Wellness & Fitness', 'Mental Health Care',
    // Professional Services
    'Management Consulting', 'Legal Services', 'Human Resources', 'Staffing & Recruiting',
    'Marketing & Advertising', 'Public Relations', 'Design', 'Architecture & Planning',
    // Industrial & Manufacturing
    'Manufacturing', 'Automotive', 'Aviation & Aerospace', 'Chemicals', 'Construction',
    'Electrical & Electronic Manufacturing', 'Mechanical & Industrial Engineering', 'Mining & Metals',
    // Consumer & Retail
    'Retail', 'Consumer Goods', 'Consumer Electronics', 'Food & Beverages', 'Restaurants',
    'Hospitality', 'Apparel & Fashion', 'Luxury Goods & Jewelry', 'Sporting Goods',
    // Media & Entertainment
    'Media', 'Entertainment', 'Music', 'Publishing', 'Online Media', 'Broadcasting',
    'Gaming', 'Film & Motion Pictures',
    // Education
    'Education', 'Higher Education', 'E-Learning', 'Research',
    // Energy & Environment
    'Energy', 'Oil & Gas', 'Renewables & Environment', 'Utilities',
    // Real Estate & Property
    'Real Estate', 'Commercial Real Estate', 'Property Management',
    // Transportation & Logistics
    'Transportation', 'Logistics & Supply Chain', 'Maritime', 'Warehousing',
    // Government & Nonprofit
    'Government Administration', 'Nonprofit Organization Management', 'Civic & Social Organization',
    'International Affairs', 'Defense & Space',
    // Agriculture
    'Agriculture', 'Farming', 'Food Production',
    // Other
    'Other',
];

export const SIZES = ['startup', 'smb', 'mid-market', 'enterprise'];

// Canonical Investor Type options — keep in sync with backend
// models/account.py INVESTOR_TYPES and data_import.py ACCOUNT_FIELDS.
export const INVESTOR_TYPES = ['Angel', 'Venture Capital', 'Private Equity', 'Family Office', 'Institutional'];

export const EMPLOYEE_RANGES = [
    { range: '1-10',         category: 'Rabbits',   size: 'SMB',        color: 'bg-emerald-50 text-emerald-700' },
    { range: '11-20',        category: 'Rabbits',   size: 'SMB',        color: 'bg-emerald-50 text-emerald-700' },
    { range: '21-50',        category: 'Rabbits',   size: 'SMB',        color: 'bg-emerald-50 text-emerald-700' },
    { range: '51-100',       category: 'Deers',     size: 'SMB',        color: 'bg-blue-50 text-blue-700' },
    { range: '101-200',      category: 'Deers',     size: 'SMB',        color: 'bg-blue-50 text-blue-700' },
    { range: '201-500',      category: 'Elephants', size: 'Mid-Market', color: 'bg-violet-50 text-violet-700' },
    { range: '501-1,000',    category: 'Elephants', size: 'Mid-Market', color: 'bg-violet-50 text-violet-700' },
    { range: '1,001-2,000',  category: 'Elephants', size: 'Enterprise', color: 'bg-indigo-50 text-indigo-700' },
    { range: '2,001-5,000',  category: 'Whales',    size: 'Enterprise', color: 'bg-amber-50 text-amber-700' },
    { range: '5,001-10,000', category: 'Whales',    size: 'Enterprise', color: 'bg-amber-50 text-amber-700' },
    { range: '10,000+',      category: 'Whales',    size: 'Enterprise', color: 'bg-amber-50 text-amber-700' },
];

export const STATUSES = [
    { value: 'target', label: 'Target', color: 'bg-slate-100 text-slate-700 border-slate-300', desc: 'Identified as a potential fit but no outreach yet. Matches your ICP criteria.' },
    { value: 'lead', label: 'Lead', color: 'bg-blue-50 text-blue-700 border-blue-300', desc: 'Initial engagement started — inbound inquiry, downloaded content, or first outreach sent.' },
    { value: 'prospect', label: 'Prospect', color: 'bg-indigo-50 text-indigo-700 border-indigo-300', desc: 'Qualified and actively being worked. A meeting has been held or discovery is underway.' },
    { value: 'opportunity', label: 'Opportunity', color: 'bg-violet-50 text-violet-700 border-violet-300', desc: 'Active deal in pipeline. Proposal sent, negotiations in progress, or in evaluation stage.' },
    { value: 'customer', label: 'Customer', color: 'bg-emerald-50 text-emerald-700 border-emerald-300', desc: 'Signed deal, active paying customer. Onboarding complete or in progress.' },
    { value: 'churned', label: 'Churned', color: 'bg-red-50 text-red-700 border-red-300', desc: 'Former customer who cancelled or did not renew. May be a candidate for win-back campaigns. Track reason for churn and last activity date.' },
    { value: 'partner', label: 'Partner', color: 'bg-stone-900 text-white border-stone-900', desc: 'Strategic partner, referral source, reseller, or integration partner. Not a direct customer but a business relationship. Track partnership type and joint revenue.' },
];

export const STATUS_VALUES = STATUSES.map(s => s.value);

/**
 * Fold a stored `status` onto the canonical lowercase vocabulary.
 *
 * The retired AccountDetail edit dialog wrote capitalised values
 * (`Prospect`, `Customer`, `Churned`), which no longer match any chip in
 * `StatusSelector` or any option in the Accounts page status filter. Matching
 * case-insensitively lets those records display correctly and re-persist in
 * canonical form on the next save.
 *
 * Values with no canonical counterpart (`Active` / `Inactive` — still written
 * today by `POST /api/admin/target-accounts/{id}/convert`) are passed through
 * untouched rather than guessed at, so an edit never silently reclassifies a
 * company. Mapping those needs a product decision plus a data migration.
 *
 * @param {string|undefined|null} value
 * @returns {string|undefined|null}
 */
export function normalizeStatus(value) {
    if (typeof value !== 'string') return value;
    return STATUS_VALUES.find(v => v === value.toLowerCase()) || value;
}

/**
 * Normalize an `industry` value to an array for the IndustryMultiSelect.
 *
 * The backend (`runtime/backend/models/account.py`) declares `industry` as
 * `Optional[Any]` and accepts a string OR a list ("str or List[str] for
 * multi-industry"), so stored values may be either shape depending on whether
 * they were written by the create flow (list) or legacy imports/edits (string).
 * This keeps the multi-select working regardless of how the value was stored.
 *
 * @param {string|string[]|undefined|null} value
 * @returns {string[]}
 */
export function toIndustryArray(value) {
    if (Array.isArray(value)) return value;
    if (value) return [value];
    return [];
}
